/**
 * <Modal> — canonical centered dialog.
 *
 * Built on @radix-ui/react-dialog per locked decision A.2 (Adversary):
 * Radix gives us battle-tested focus trap, focus restore, ESC handling,
 * scroll lock, and ARIA wiring out of the box. Resolves Phase-1 findings:
 *   C4 — 99 rogue `fixed inset-0 backdrop` modals
 *   C7 — 80% of modals don't trap focus or restore on close
 *
 * Composition (compound API):
 *
 *   <Modal open={isOpen} onOpenChange={setOpen} size='md'>
 *     <Modal.Header
 *       title='Delete work order'
 *       description='This cannot be undone.'
 *     />
 *     <Modal.Body>
 *       <Text>Confirm deletion of WO-1042 …</Text>
 *     </Modal.Body>
 *     <Modal.Footer>
 *       <Button variant='secondary' onClick={() => setOpen(false)}>Cancel</Button>
 *       <Button variant='destructive' onClick={handleDelete}>Delete</Button>
 *     </Modal.Footer>
 *   </Modal>
 *
 * Form-based modals: wrap `Modal.Body`/`Modal.Footer` in `<Modal.Form>`
 * instead of a bare `<form>` — see `Modal.Form`'s own doc comment for why
 * a bare `<form>` silently breaks `Modal.Body`'s scroll chain.
 *
 * Footer order is locked: Cancel-left, primary-right (Phase-1 finding M6).
 *
 * Sizes: sm (400px) / md (560px / default) / lg (760px) / full (95vw, 95dvh).
 *
 * Default behavior:
 *   - ESC closes (Radix default)
 *   - Backdrop click closes — disable via onPointerDownOutside={e => e.preventDefault()}
 *   - Focus traps inside the modal until close
 *   - Focus returns to the trigger element on close
 *
 * Run 2 (mobile-remediation) additions — resolves audit findings F-01/F-08:
 *   - `dvh` instead of `vh` for max-height (accounts for mobile browser chrome).
 *   - Panel height also tracks `window.visualViewport` live (via
 *     `useVisualViewport`) so it shrinks further when the on-screen keyboard
 *     opens, instead of getting capped by an outdated `vh` snapshot.
 *   - `mobileSheet` — opt-in prop implementing the design-system spec's
 *     "< md = full-screen, no radius" requirement (docs/design-system/
 *     12-accessibility-responsiveness-performance.md §12.2). Off by default
 *     so every existing consumer keeps its current desktop-and-mobile
 *     centered-dialog behavior unchanged (Decision — see docs/audits/MOBILE_AUDIT.md
 *     §Decisions D-4); opt in per modal where a full-screen sheet is the
 *     better mobile treatment (long forms, dense checklists).
 *   - `Modal.Footer` now renders via the shared `<ActionFooter>` shell —
 *     adds safe-area-aware bottom padding (`env(safe-area-inset-bottom)`),
 *     a no-op on non-notched devices.
 *
 * Spec: docs/design-system/08-modals-drawers-feedback.md.
 */

import * as RadixDialog from '@radix-ui/react-dialog';
import { type ReactNode, type HTMLAttributes, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Heading } from '../../primitives/Heading';
import { Text } from '../../primitives/Text';
import { Icon } from '../../primitives/Icon';
import { ActionFooter } from '../ActionFooter';
import { useVisualViewport } from '../../hooks/useVisualViewport';

/** Matches Tailwind's default `md` breakpoint (`max-md:` = below 768px). */
const MOBILE_SHEET_MEDIA_QUERY = '(max-width: 767.98px)';

/**
 * Tracks whether the viewport is currently below the `md` breakpoint, so JS
 * sizing (inline `style.maxHeight`) can be gated to match the `max-md:`
 * CSS classes `MOBILE_SHEET_CLASSES` already uses. Inline styles win over
 * CSS classes regardless of media query, so without this the mobile-sheet
 * 100% height budget leaked into the desktop layout (Decision D-5 fix —
 * see docs/audits/MOBILE_AUDIT.md §Decisions).
 */
function useIsBelowMdBreakpoint(): boolean {
  const [isBelowMd, setIsBelowMd] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_SHEET_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MOBILE_SHEET_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsBelowMd(e.matches);
    setIsBelowMd(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  return isBelowMd;
}

export type ModalSize = 'sm' | 'md' | 'lg' | 'full';

export interface ModalProps {
  /** Controlled open state. */
  open: boolean;
  /** Notified when Radix wants to open or close (ESC, click outside, X button). */
  onOpenChange: (open: boolean) => void;
  size?: ModalSize;
  /** Disable backdrop-click-to-close. ESC still works. */
  disableBackdropClose?: boolean;
  /** Disable ESC-to-close (rare — confirm dialogs may want this). */
  disableEscClose?: boolean;
  /**
   * Render full-screen (`inset-0`, no radius, no centering) below the `md`
   * breakpoint per the design-system's mobile modal spec. Unaffected at
   * `md` and above — the modal keeps its normal centered `size`. Default
   * `false` (opt-in — see the module doc comment).
   */
  mobileSheet?: boolean;
  /** Optional className on the panel. */
  className?: string;
  /**
   * ARIA role override. Default `'dialog'`. Pass `'alertdialog'` for
   * modals that interrupt the user with a decision they must act on
   * (matches Radix's own default — this is a straight passthrough since
   * `RadixDialog.Content` sets `role="dialog"` before spreading caller
   * props, so an explicit `role` here wins).
   */
  role?: 'dialog' | 'alertdialog';
  children: ReactNode;
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'w-[min(92vw,400px)]',
  md: 'w-[min(92vw,560px)]',
  lg: 'w-[min(92vw,760px)]',
  full: 'w-[95vw] h-[95dvh]',
};

const MOBILE_SHEET_CLASSES =
  'max-md:left-0 max-md:top-0 max-md:translate-x-0 max-md:translate-y-0 ' +
  'max-md:h-[100dvh] max-md:max-h-[100dvh] max-md:w-full max-md:rounded-none max-md:border-0';

function ModalRoot({
  open,
  onOpenChange,
  size = 'md',
  disableBackdropClose,
  disableEscClose,
  mobileSheet = false,
  className,
  role,
  children,
}: ModalProps) {
  // Keyboard-aware sizing (F-01/F-08): shrink the panel's max-height to a
  // fraction of the live `visualViewport` height rather than a static `dvh`
  // snapshot, so it keeps shrinking as the on-screen keyboard opens instead
  // of leaving the footer covered. `dvh` alone does NOT react to the
  // keyboard (only to browser-chrome show/hide) — this is the gap `dvh`
  // can't close on its own. `size='full'` keeps its slightly taller 95%
  // budget; `mobileSheet` uses the full 100% budget, but ONLY below `md`
  // (matched via `useIsBelowMdBreakpoint`, mirroring the `max-md:` classes
  // in `MOBILE_SHEET_CLASSES`) — at `md` and above it falls back to the
  // standard 90% cap so the desktop-centered treatment is unaffected (see
  // Decision D-5 in docs/audits/MOBILE_AUDIT.md §Decisions). Inline `style` always
  // wins over CSS classes, so without this gate a `mobileSheet` modal's
  // desktop max-height silently grew from 90% to 100% too.
  const vv = useVisualViewport(open);
  const isBelowMd = useIsBelowMdBreakpoint();
  const maxHeightPct = mobileSheet && isBelowMd ? 1 : size === 'full' ? 0.95 : 0.9;
  const dynamicMaxHeight = vv.height != null ? Math.floor(vv.height * maxHeightPct) : undefined;

  // A11Y-KB-02 fix (2026-08-08, P2 keyboard/SR tail): Radix's OWN default
  // close-focus behavior (`@radix-ui/react-dialog`'s `DialogContentModal`)
  // does `event.preventDefault(); context.triggerRef.current?.focus();` —
  // which (a) blocks `FocusScope`'s generic "restore focus to whatever was
  // focused before the scope activated" fallback, and (b) only actually
  // restores anything if the opener was a `<RadixDialog.Trigger>`. This
  // component never renders one (every call site opens the modal from its
  // OWN plain `<Button onClick={...}>`, per this file's own doc comment's
  // canonical example) — so `context.triggerRef` is always empty and the
  // net effect, for every consumer, is that closing a Modal silently drops
  // focus to `<body>` instead of "returns to the trigger element on close"
  // as documented above. Capture whatever had focus right before the modal
  // opened and restore it ourselves; passing `onCloseAutoFocus` here runs
  // BEFORE Radix's own default (see `composeEventHandlers` — the caller-
  // supplied handler runs first, and calling `preventDefault()` there skips
  // Radix's default entirely), so this replaces rather than races with it.
  const openerElementRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      openerElementRef.current = document.activeElement as HTMLElement | null;
    }
  }, [open]);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className="fixed inset-0 z-[9999] bg-overlay backdrop-blur-sm data-[state=open]:animate-fade-in"
        />
        <RadixDialog.Content
          // Only pass `role` when explicitly set — Radix's own Content sets
          // `role: "dialog"` as a default before spreading caller props, so
          // an explicit `role={undefined}` key here would silently clobber
          // that default (object spread applies undefined values too).
          {...(role ? { role } : {})}
          // This @radix-ui/react-dialog version never sets `aria-modal`
          // itself (checked against dist source — genuinely absent, not a
          // false negative). Every Modal usage in this codebase is modal
          // (Root's `modal` prop is never overridden from its `true`
          // default), so this is always correct to set statically.
          aria-modal="true"
          onCloseAutoFocus={(e) => {
            const opener = openerElementRef.current;
            if (opener && document.contains(opener) && typeof opener.focus === 'function') {
              e.preventDefault();
              opener.focus();
            }
          }}
          onPointerDownOutside={(e) => {
            if (disableBackdropClose) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (disableEscClose) e.preventDefault();
          }}
          style={dynamicMaxHeight != null ? { maxHeight: `${dynamicMaxHeight}px` } : undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-[10000] -translate-x-1/2 -translate-y-1/2',
            'flex flex-col bg-surface text-text rounded-xl shadow-modal border border-border-subtle',
            'data-[state=open]:animate-scale-in',
            'max-h-[90dvh] overflow-hidden',
            SIZE_CLASSES[size],
            mobileSheet && MOBILE_SHEET_CLASSES,
            className,
          )}
        >
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
ModalRoot.displayName = 'Modal';

interface ModalHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  /** Show the X close button. Default true. */
  showClose?: boolean;
  /** Aria-label for the close button. Default 'Close dialog'. */
  closeLabel?: string;
}

function ModalHeader({
  title,
  description,
  showClose = true,
  closeLabel = 'Close dialog',
  className,
  ...rest
}: ModalHeaderProps) {
  return (
    <div className={cn('flex items-start justify-between gap-3 px-5 pt-5 pb-3', className)} {...rest}>
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <RadixDialog.Title asChild>
          <Heading level={3}>{title}</Heading>
        </RadixDialog.Title>
        {description && (
          <RadixDialog.Description asChild>
            <Text variant="body-sm" tone="muted">
              {description}
            </Text>
          </RadixDialog.Description>
        )}
      </div>
      {showClose && (
        <RadixDialog.Close
          aria-label={closeLabel}
          className={cn(
            'shrink-0 rounded-md p-1 text-muted hover:bg-elevated hover:text-text',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
          )}
        >
          <Icon as={X} size="sm" label={closeLabel} />
        </RadixDialog.Close>
      )}
    </div>
  );
}
ModalHeader.displayName = 'Modal.Header';

function ModalBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  // tabIndex={0} satisfies WCAG 2.1.1 (keyboard) for scrollable regions —
  // see Drawer.Body for the same fix. Without it, axe flags the body as
  // a scrollable region with no keyboard access (rule:
  // scrollable-region-focusable).
  return (
    <div
      role="group"
      aria-label="Modal content"
      // tabIndex={0} is required so keyboard-only users can arrow-scroll this
      // overflow region (axe: scrollable-region-focusable / WCAG 2.1.1).
      // Removing it to satisfy no-noninteractive-tabindex would reintroduce
      // that violation, so the rule is intentionally disabled here.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      className={cn('flex-1 overflow-y-auto px-5 py-3 text-text focus:outline-none', className)}
      {...rest}
    >
      {children}
    </div>
  );
}
ModalBody.displayName = 'Modal.Body';

function ModalFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  // Delegates to the shared <ActionFooter> shell (F-01) — adds additive
  // safe-area-aware bottom padding on top of the same visual chrome this
  // rendered directly before. Footer order (Cancel-left/primary-right)
  // stays the caller's responsibility.
  return (
    <ActionFooter variant="panel" className={className} {...rest}>
      {children}
    </ActionFooter>
  );
}
ModalFooter.displayName = 'Modal.Footer';

/**
 * `Modal.Form` — the flex-chain-safe wrapper for form-based modals.
 *
 * The panel (`ModalRoot`) is `flex flex-col ... overflow-hidden` and
 * `Modal.Body` scrolls via `flex-1 overflow-y-auto`, but `flex-1` only
 * resolves against a flex parent, and a flex child won't shrink below its
 * content unless it's also `min-h-0`. Wrapping `Modal.Body`/`Modal.Footer`
 * in a bare `<form>` — with no flex classes of its own — breaks that chain
 * at the form: `Modal.Body`'s `flex-1` has nothing to size against, the
 * form itself refuses to shrink, and the panel's `overflow-hidden` clips
 * the overflow instead of `Modal.Body` scrolling it.
 *
 * This bit 13 independent call sites (fixed 2026-08-11, see the
 * `fix(modal): restore Modal.Body scroll chain through bare <form>
 * wrappers` commit) with no compiler or lint signal — a `<form>` is a
 * perfectly valid JSX element, so nothing caught the missing classes.
 * `Modal.Form` bakes `flex flex-col min-h-0 flex-1` in so callers can't
 * forget them again; use it instead of a raw `<form>` whenever a modal's
 * body/footer need to live inside a form element.
 */
function ModalForm({ className, children, ...rest }: HTMLAttributes<HTMLFormElement>) {
  return (
    <form className={cn('flex flex-col min-h-0 flex-1', className)} {...rest}>
      {children}
    </form>
  );
}
ModalForm.displayName = 'Modal.Form';

type ModalComponent = typeof ModalRoot & {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
  Form: typeof ModalForm;
};

export const Modal = ModalRoot as ModalComponent;
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Form = ModalForm;

export default Modal;
